import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, TrendingUp, Shield, Building2, Users, DollarSign,
  CheckCircle2, ChevronRight, BarChart3, Home, Briefcase, Target,
  PieChart, FileText, Phone, MapPin, Clock, Lock, LineChart,
  Wallet, Award, Landmark, Scale
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { fundDetails } from '../data/mock';

const HighNetWorthPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Passive income benefits
  const passiveIncomeBenefits = [
    {
      icon: Wallet,
      title: 'Quarterly Distributions',
      description: 'Regular cash flow from income-producing multifamily and commercial assets'
    },
    {
      icon: PieChart,
      title: 'Portfolio Diversification',
      description: 'Low correlation with public equities for reduced overall portfolio volatility'
    },
    {
      icon: Shield,
      title: 'Inflation Hedge',
      description: 'Real assets that historically preserve purchasing power over time'
    },
    {
      icon: TrendingUp,
      title: 'Long-Term Appreciation',
      description: 'Value creation through strategic improvements and market growth'
    }
  ];

  // Institutional access features
  const institutionalFeatures = [
    {
      icon: Target,
      title: 'Proprietary Deal Sourcing',
      description: 'Off-market opportunities through established relationships'
    },
    {
      icon: BarChart3,
      title: 'Institutional Underwriting',
      description: 'Conservative analysis with built-in margin of safety'
    },
    {
      icon: Building2,
      title: 'Professional Management',
      description: 'In-house operations team for hands-free investing'
    },
    {
      icon: Scale,
      title: 'Aligned Incentives',
      description: 'GP co-investment ensures partnership alignment'
    }
  ];

  // Value-add strategy steps
  const valueAddSteps = [
    { step: '01', title: 'Acquire Below Market', description: 'Target distressed and undermanaged assets' },
    { step: '02', title: 'Renovate & Improve', description: 'Strategic unit and amenity upgrades' },
    { step: '03', title: 'Optimize Operations', description: 'Expense reduction and revenue growth' },
    { step: '04', title: 'Exit at Premium', description: 'Disposition timing for maximum value' }
  ];

  // Investment process steps
  const investmentSteps = [
    { number: '1', title: 'Initial Consultation', description: 'Discuss your investment goals and timeline' },
    { number: '2', title: 'Document Review', description: 'Review PPM and subscription materials' },
    { number: '3', title: 'Accreditation', description: 'Verify accredited investor status' },
    { number: '4', title: 'Fund & Close', description: 'Complete subscription and fund commitment' }
  ];

  // Reporting features
  const reportingFeatures = [
    { icon: FileText, text: 'Quarterly performance reports with property-level detail' },
    { icon: Lock, text: 'Secure investor portal with 24/7 document access' },
    { icon: Phone, text: 'Direct access to investment team' },
    { icon: BarChart3, text: 'Annual K-1 tax documents delivered on schedule' }
  ];

  // Houston market stats
  const houstonStats = [
    { value: '7M+', label: 'Metro Population' },
    { value: '#1', label: 'Job Growth' },
    { value: '$512B', label: 'Regional GDP' },
    { value: '15+', label: 'Years Experience' }
  ];

  return (
    <>
      <Helmet>
        <title>Real Estate Investments for High Net Worth Individuals | ASAP Capital</title>
        <meta 
          name="description" 
          content="Value-add real estate investment opportunities for high net worth individuals seeking passive income and diversification across Texas markets." 
        />
        <meta name="keywords" content="real estate investments for high net worth individuals, private equity real estate funds, passive real estate investing, investor opportunities, Houston real estate investments" />
        <link rel="canonical" href="/investors/high-net-worth" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-24 lg:pt-44 lg:pb-32 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-[#D4AF37]/5 rounded-full blur-[180px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D4AF37]/3 rounded-full blur-[150px] pointer-events-none" />
            
            {/* Animated grid pattern */}
            <div className="absolute inset-0 opacity-[0.03]">
              <div className="absolute inset-0" style={{
                backgroundImage: `linear-gradient(rgba(212,175,55,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.3) 1px, transparent 1px)`,
                backgroundSize: '60px 60px'
              }} />
            </div>
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl"
              >
                {/* Pill badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <Users className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">For investors</span>
                </motion.div>

                {/* H1 */}
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                  variants={itemVariants}
                >
                  Real Estate Investment Opportunities for{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    High Net Worth Individuals
                  </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p 
                  className="text-base lg:text-lg text-white/60 leading-relaxed mb-8 max-w-2xl"
                  variants={itemVariants}
                >
                  Access institutional-grade real estate investments with passive income potential, 
                  portfolio diversification, and professional management—starting at $100,000.
                </motion.p>

                {/* Key stats inline */}
                <motion.div 
                  className="flex flex-wrap gap-6 mb-10"
                  variants={itemVariants}
                >
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white/70 text-sm">$100K Minimum</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white/70 text-sm">20-28% Target IRR</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white/70 text-sm">8% Preferred Return</span>
                  </div>
                </motion.div>

                {/* CTA buttons */}
                <motion.div 
                  className="flex flex-wrap gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                      Schedule Consultation
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                  <Link to="/fund-terms">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                      View Fund Terms
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: PASSIVE INCOME & DIVERSIFICATION ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Why Real Estate
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Passive Income & Diversification
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      Real estate offers high net worth investors a proven path to passive income and 
                      portfolio stability. Unlike volatile public markets, income-producing properties 
                      generate consistent cash flow while building long-term equity.
                    </motion.p>
                    
                    <motion.div 
                      className="grid sm:grid-cols-2 gap-4"
                      variants={itemVariants}
                    >
                      {passiveIncomeBenefits.map((benefit, index) => (
                        <motion.div
                          key={benefit.title}
                          className="bg-[#111D35]/50 rounded-xl p-4 border border-white/10 hover:border-[#D4AF37]/20 transition-all group"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ y: -3 }}
                        >
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mb-3 group-hover:bg-[#D4AF37]/20 transition-colors">
                            <benefit.icon className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <h4 className="text-white font-semibold text-sm mb-1">{benefit.title}</h4>
                          <p className="text-white/40 text-xs">{benefit.description}</p>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  {/* Visual card */}
                  <motion.div 
                    variants={fadeInUp}
                    className="relative"
                  >
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      {/* Animated background */}
                      <div className="absolute top-0 right-0 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#D4AF37]/5 rounded-full blur-2xl" />
                      
                      <div className="relative z-10">
                        <div className="text-center mb-8">
                          <span className="text-white/40 text-sm uppercase tracking-wider">Target Returns</span>
                          <div className="mt-4">
                            <span className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
                              20-28%
                            </span>
                            <span className="text-white/50 text-lg ml-2">IRR</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                          <div className="text-center">
                            <p className="text-3xl font-bold text-white">1.8-2.5x</p>
                            <p className="text-white/40 text-sm mt-1">Equity Multiple</p>
                          </div>
                          <div className="text-center">
                            <p className="text-3xl font-bold text-emerald-400">8%</p>
                            <p className="text-white/40 text-sm mt-1">Preferred Return</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: INSTITUTIONAL-GRADE ACCESS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Exclusive Access
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Institutional-Grade Investments
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Access professionally managed real estate opportunities typically reserved for 
                    large institutions—with lower minimums and direct sponsor relationships.
                  </motion.p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {institutionalFeatures.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 group relative overflow-hidden"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -8, boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}
                    >
                      {/* Hover gradient */}
                      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <div className="relative z-10">
                        <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 transition-colors border border-[#D4AF37]/20">
                          <feature.icon className="h-7 w-7 text-[#D4AF37]" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                          {feature.title}
                        </h3>
                        <p className="text-white/50 text-sm">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="text-center mt-10"
                  variants={itemVariants}
                >
                  <Link to="/strategy">
                    <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2 mx-auto">
                      Explore Investment Strategy
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: VALUE-ADD STRATEGY ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Visual - Value Add Timeline */}
                  <motion.div variants={fadeInUp}>
                    <div className="relative">
                      {/* Connection line */}
                      <div className="absolute left-[27px] top-8 bottom-8 w-0.5 bg-gradient-to-b from-[#D4AF37] via-[#D4AF37]/50 to-[#D4AF37]/20" />
                      
                      <div className="space-y-6">
                        {valueAddSteps.map((step, index) => (
                          <motion.div
                            key={step.step}
                            className="flex gap-5 items-start"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.15 }}
                          >
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#D4AF37]/20 relative z-10">
                              <span className="text-[#0A1628] font-bold text-sm">{step.step}</span>
                            </div>
                            <div className="pt-2">
                              <h4 className="text-white font-semibold mb-1">{step.title}</h4>
                              <p className="text-white/50 text-sm">{step.description}</p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>

                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Our Approach
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Value-Add Strategy
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-6"
                      variants={itemVariants}
                    >
                      We target distressed and undermanaged properties with clear value creation 
                      potential. Through strategic renovations, operational improvements, and 
                      professional management, we drive NOI growth and maximize exit values.
                    </motion.p>
                    
                    <motion.div className="space-y-3 mb-8" variants={itemVariants}>
                      {['Unit renovations capturing rent premiums', 'Expense optimization through scale', 'Professional leasing and marketing', 'Strategic exit timing'].map((point, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                          <span className="text-white/60">{point}</span>
                        </div>
                      ))}
                    </motion.div>

                    <motion.div className="flex flex-wrap gap-3" variants={itemVariants}>
                      <Link to="/strategy">
                        <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm">
                          Full Strategy Details
                        </Button>
                      </Link>
                      <Link to="/portfolio">
                        <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full text-sm">
                          View Portfolio
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: INVESTMENT REQUIREMENTS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Getting Started
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Simple Investment Process
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      We've streamlined the investment process to make it straightforward for 
                      investors. From initial consultation to funding, our team guides 
                      you through each step.
                    </motion.p>

                    {/* Investment process steps */}
                    <div className="space-y-4">
                      {investmentSteps.map((step, index) => (
                        <motion.div
                          key={step.number}
                          className="flex items-start gap-4 p-4 bg-[#111D35]/50 rounded-xl border border-white/5 hover:border-[#D4AF37]/20 transition-colors"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 border border-[#D4AF37]/30">
                            <span className="text-[#D4AF37] font-semibold text-sm">{step.number}</span>
                          </div>
                          <div>
                            <h4 className="text-white font-semibold text-sm">{step.title}</h4>
                            <p className="text-white/40 text-xs mt-1">{step.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <motion.div className="mt-8" variants={itemVariants}>
                      <Link to="/investment-process">
                        <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2">
                          View Full Process
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </motion.div>
                  </div>

                  {/* Requirements card */}
                  <motion.div variants={fadeInUp}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                            <Landmark className="h-6 w-6 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">Investment Terms</h4>
                            <p className="text-white/40 text-sm">Fund V Overview</p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="flex justify-between items-center py-3 border-b border-white/10">
                            <span className="text-white/60">Minimum Investment</span>
                            <span className="text-white font-semibold">$100,000</span>
                          </div>
                          <div className="flex justify-between items-center py-3 border-b border-white/10">
                            <span className="text-white/60">Fund Structure</span>
                            <span className="text-white font-semibold">Reg D 506(c)</span>
                          </div>
                          <div className="flex justify-between items-center py-3 border-b border-white/10">
                            <span className="text-white/60">Hold Period</span>
                            <span className="text-white font-semibold">5-7 Years</span>
                          </div>
                          <div className="flex justify-between items-center py-3 border-b border-white/10">
                            <span className="text-white/60">Preferred Return</span>
                            <span className="text-emerald-400 font-semibold">8%</span>
                          </div>
                          <div className="flex justify-between items-center py-3">
                            <span className="text-white/60">Accredited Status</span>
                            <span className="text-[#D4AF37] font-semibold">Accredited Only</span>
                          </div>
                        </div>

                        <Link to="/fund-terms" className="block mt-6">
                          <Button className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold py-3 h-12 rounded-xl text-sm flex items-center justify-center gap-2">
                            View Complete Fund Terms
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: TRANSPARENCY & REPORTING ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Investor Experience
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Transparency & Reporting
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Stay informed with institutional-grade reporting and direct access to your 
                    investment team throughout the investment lifecycle.
                  </motion.p>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
                  {reportingFeatures.map((feature, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start gap-4 p-5 bg-[#111D35]/50 rounded-xl border border-white/10 hover:border-[#D4AF37]/20 transition-all"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ x: 5 }}
                    >
                      <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-6 w-6 text-[#D4AF37]" />
                      </div>
                      <span className="text-white/70 text-sm leading-relaxed pt-3">{feature.text}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: HOUSTON & TEXAS MARKETS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Stats grid */}
                  <motion.div 
                    className="grid grid-cols-2 gap-4"
                    variants={fadeInUp}
                  >
                    {houstonStats.map((stat, index) => (
                      <motion.div
                        key={stat.label}
                        className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 text-center"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <p className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent mb-2">
                          {stat.value}
                        </p>
                        <p className="text-white/50 text-sm">{stat.label}</p>
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Market Focus
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Houston & Texas Markets
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-6"
                      variants={itemVariants}
                    >
                      Houston's population growth, job expansion, and demand for workforce housing 
                      create exceptional opportunities for value-add investors. Our 15+ years of 
                      local experience provides unmatched market insight and deal flow.
                    </motion.p>
                    
                    <motion.div className="space-y-3 mb-8" variants={itemVariants}>
                      {['Nation-leading population and job growth', 'Business-friendly regulatory environment', 'Diverse economic base beyond energy', 'Strong demand for workforce housing'].map((point, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                          <span className="text-white/60">{point}</span>
                        </div>
                      ))}
                    </motion.div>

                    <motion.div className="flex flex-wrap gap-3" variants={itemVariants}>
                      <Link to="/why-houston">
                        <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2">
                          Why Houston
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to="/portfolio">
                        <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full text-sm">
                          View Portfolio
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: TRACK RECORD ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                {/* Background accent */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Proven Results
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Track Record of Value Creation
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-6"
                      variants={itemVariants}
                    >
                      Review our historical performance across completed investments, including 
                      realized exits, operational improvements, and case studies demonstrating 
                      our value-add execution.
                    </motion.p>

                    <motion.div className="flex flex-wrap gap-3" variants={itemVariants}>
                      <Link to="/track-record">
                        <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-5 py-2.5 h-11 rounded-full text-sm flex items-center gap-2">
                          View Track Record
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to="/portfolio">
                        <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full text-sm">
                          Case Studies
                        </Button>
                      </Link>
                    </motion.div>
                  </div>

                  {/* Stats */}
                  <motion.div 
                    className="grid grid-cols-3 gap-4"
                    variants={fadeInUp}
                  >
                    <div className="text-center p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                      <p className="text-2xl lg:text-3xl font-bold text-[#D4AF37]">70+</p>
                      <p className="text-white/40 text-xs mt-1">Deals Completed</p>
                    </div>
                    <div className="text-center p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                      <p className="text-2xl lg:text-3xl font-bold text-white">100%</p>
                      <p className="text-white/40 text-xs mt-1">Capital Returned</p>
                    </div>
                    <div className="text-center p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                      <p className="text-2xl lg:text-3xl font-bold text-emerald-400">28%</p>
                      <p className="text-white/40 text-xs mt-1">Avg. IRR</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FINAL CTA ==================== */}
          <section className="py-24 lg:py-32 relative bg-[#111D35]">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d1a2d] via-transparent to-[#0d1a2d]" />
            
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative z-10">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="text-center"
              >
                <motion.span 
                  className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                  variants={itemVariants}
                >
                  Get Started
                </motion.span>
                <motion.h2 
                  className="text-2xl lg:text-4xl font-bold text-white mb-5"
                  variants={itemVariants}
                >
                  Start Your Investment With ASAP Capital
                </motion.h2>
                <motion.p 
                  className="text-white/50 max-w-xl mx-auto mb-10"
                  variants={itemVariants}
                >
                  Schedule a consultation to discuss how our value-add real estate strategy 
                  aligns with your investment objectives and portfolio goals.
                </motion.p>
                
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-8 py-3 h-14 rounded-full text-base">
                      Schedule Consultation
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </Link>
                  <Link to="/fund-terms">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-14 rounded-full text-base">
                      Request Investment Materials
                    </Button>
                  </Link>
                </motion.div>

                {/* Compliance note */}
                <motion.p 
                  className="text-white/30 text-xs mt-10 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  ASAP Capital investments are available exclusively to investors as defined 
                  by SEC regulations. All investments involve risk including potential loss of principal. 
                  Past performance is not indicative of future results.
                </motion.p>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default HighNetWorthPage;
